import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Export } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/ui/button/Button";
import { PAGE_TITLES } from "~/configs/pageTitles";

import ExportsPage, { CreateActionContext } from "../ExportsPage/ExportsPage";
import styles from "./ClientReports.module.scss";
import CreateClientReportModal, {
  ClientReportRequest,
} from "./CreateClientReportModal";
import GoogleIntegrationsNotice from "./GoogleIntegrationsNotice";
import ReportDeliveryCard from "./ReportDeliveryCard";
import { formatPeriod, fromApiPeriod, toApiPeriod } from "./period";
import { ReportDelivery, useReportDelivery } from "./useReportDelivery";

const PERIOD_COLUMN: ColumnDef<Export> = {
  id: "period",
  header: "Period",
  size: 260,
  enableSorting: false,
  cell: ({ row }) => {
    const period = fromApiPeriod(row.original.starts_on, row.original.ends_on);

    return (
      <span className={styles.periodColumn}>
        {period ? formatPeriod(period) : "—"}
      </span>
    );
  },
};
const EXTRA_COLUMNS = [PERIOD_COLUMN];

const ClientReports = () => {
  const { id } = useParams();
  const siteId = Number(id);
  const delivery = useReportDelivery(siteId);

  return (
    <ExportsPage<ClientReportRequest>
      exportType="ClientReport"
      createFn={(siteId, { period, email }) =>
        apiClient.sites.clientReportsCreate(siteId, {
          ...toApiPeriod(period),
          email,
          // The site's list, kept on its schedule, gets emailed reports too.
          report_recipient_list_id:
            email && delivery.listId != null ? delivery.listId : undefined,
        })
      }
      pageTitle={PAGE_TITLES.clientReports}
      description="Create a PDF report to share with your client, branded with your white-label profile if you have one. It covers the work done, keyword rankings, Search Console and Analytics traffic, backlinks, search engine and AI crawler visits and technical health for the period you pick, each compared with the period of the same length right before it, and ends with suggested next steps. Team admins can also have a report created every week or month and emailed to the team members they choose and to a list of other addresses, such as your client's."
      successMessage="Client report is generating"
      emptyText="Create your first client report to get started."
      extraColumns={EXTRA_COLUMNS}
      renderAboveTable={({ canCreate }) => (
        <>
          {canCreate && <GoogleIntegrationsNotice siteId={siteId} />}
          <ReportDeliveryCard siteId={siteId} delivery={delivery} />
        </>
      )}
      renderCreateAction={(context) => (
        <CreateClientReportAction {...context} delivery={delivery} />
      )}
    />
  );
};

// The backend also refuses a period requested in the last hour (422); that
// depends on the period picked, so the dialog checks it before sending.
const CreateClientReportAction = ({
  create,
  isCreating,
  canCreate,
  exports,
  delivery,
}: CreateActionContext<ClientReportRequest> & { delivery: ReportDelivery }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button onClick={() => setIsOpen(true)} disabled={!canCreate}>
        Create New Report
      </Button>
      <CreateClientReportModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isCreating={isCreating}
        exports={exports}
        canEmail={delivery.canManage}
        recipientNames={delivery.recipientNames}
        listLabel={delivery.listLabel}
        onSubmit={(request) =>
          create(request, { onSuccess: () => setIsOpen(false) })
        }
      />
    </>
  );
};

export default ClientReports;
